import React from "react";
import { AlertCircle, X, Target, ArrowRight, MessageCircle } from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const problemFacts = [
  "A lead messages you. You reply.",
  "They ask something. You respond.",
  "And then… silence.",
  "No follow-up. No structure. No conversion.",
];

const villainMisconceptions = [
  "“Just send broadcasts.”",
  "“Automate replies.”",
  "“Run campaigns.”",
];

const villainMissing = [
  "Where WhatsApp fits in your business",
  "What to say at each stage",
  "When to follow up",
  "How to convert the conversation",
];

function PrimaryCTA({ testId, waTestId, label = "Book a Strategy Call" }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <a
        href={BOOK_CALL_URL}
        target="_blank"
        rel="noreferrer"
        data-testid={testId}
        className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold transition-all hover:scale-[1.03]"
        style={{ background: "var(--gold)", color: "var(--ink)" }}
      >
        {label}
        <ArrowRight
          size={17}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </a>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        data-testid={waTestId}
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold text-white transition-all hover:scale-[1.03]"
        style={{ background: "var(--whatsapp)" }}
      >
        <MessageCircle size={17} strokeWidth={2.5} />
        Chat on WhatsApp
      </a>
    </div>
  );
}

export default function ProblemBlock() {
  return (
    <>
      {/* PROBLEM — cream */}
      <section
        id="problem"
        data-testid="problem-section"
        className="relative py-20 lg:py-28"
        style={{ background: "var(--cream)" }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.1] max-w-4xl"
            data-testid="problem-headline"
          >
            Let’s be honest about{" "}
            <span className="font-display-italic">what’s really happening</span>.
          </h2>

          <div className="mt-12 lg:mt-14 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            <div className="space-y-4">
              {problemFacts.map((p, i) => (
                <div
                  key={i}
                  className="flex gap-4 items-start rounded-2xl px-5 py-4 bg-white card-hover"
                  style={{ border: "1px solid var(--line)" }}
                  data-testid={`problem-fact-${i}`}
                >
                  <span
                    className="shrink-0 mt-0.5 w-8 h-8 rounded-full inline-flex items-center justify-center font-display-italic text-base"
                    style={{
                      background: "rgba(212,164,76,0.12)",
                      color: "var(--gold-dark)",
                    }}
                  >
                    {i + 1}
                  </span>
                  <p className="text-base lg:text-lg text-[var(--ink)] leading-snug pt-1">
                    {p}
                  </p>
                </div>
              ))}
            </div>

            <div
              className="rounded-3xl p-8 lg:p-10 relative bg-white card-hover"
              style={{
                boxShadow:
                  "0 30px 60px -30px rgba(31,35,48,0.18), 0 2px 8px -2px rgba(31,35,48,0.05)",
              }}
            >
              <AlertCircle className="text-[var(--gold-dark)]" size={28} />
              <p className="mt-5 text-xl lg:text-2xl font-display text-[var(--ink)] leading-snug">
                So you assume:{" "}
                <span className="font-display-italic">
                  “Leads are not serious.”
                </span>
              </p>
              <p
                className="mt-3 text-base lg:text-lg leading-relaxed"
                style={{ color: "var(--text-muted)" }}
              >
                But the truth is brutal —
              </p>
              <p className="mt-4 text-2xl font-display-italic leading-snug">
                You’re losing revenue inside the conversations you already have.
              </p>
            </div>
          </div>

          <div className="mt-12 lg:mt-14 flex justify-center">
            <PrimaryCTA testId="problem-cta" waTestId="problem-whatsapp-cta" />
          </div>
        </div>
      </section>

      {/* VILLAIN — white */}
      <section
        data-testid="villain-section"
        className="relative py-20 lg:py-28"
        style={{ background: "#ffffff" }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.1] max-w-4xl"
            data-testid="villain-headline"
          >
            WhatsApp API didn’t fail you.{" "}
            <span className="font-display-italic">
              You were never shown how to use it.
            </span>
          </h2>

          <div className="mt-12 lg:mt-14 grid lg:grid-cols-2 gap-6 lg:gap-7">
            <div
              className="rounded-3xl p-8 lg:p-10 bg-white card-hover"
              style={{
                border: "1px solid var(--line)",
                boxShadow: "0 30px 60px -30px rgba(31,35,48,0.15)",
              }}
            >
              <p className="font-display text-xl text-[var(--ink)] mb-5">
                You were told:
              </p>
              <div className="space-y-4">
                {villainMisconceptions.map((m, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3"
                    data-testid={`villain-told-${i}`}
                  >
                    <X size={18} className="text-[#c94b4b] shrink-0" />
                    <span className="text-lg text-[var(--ink)]">{m}</span>
                  </div>
                ))}
              </div>
              <div
                className="mt-7 pt-6 border-t"
                style={{ borderColor: "var(--line)" }}
              >
                <p
                  className="text-sm uppercase tracking-wider font-semibold mb-3"
                  style={{ color: "var(--text-muted)" }}
                >
                  So it becomes
                </p>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: "var(--text-muted)" }}
                >
                  another tool. another expense. another missed opportunity.
                </p>
              </div>
            </div>

            <div
              className="rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden card-hover-dark"
              style={{ background: "var(--ink)" }}
            >
              <div className="grain absolute inset-0" />
              <div className="relative">
                <Target className="text-[var(--gold-light)]" size={28} />
                <p
                  className="font-display text-xl mt-5"
                  style={{ color: "var(--cream)" }}
                >
                  But nobody told you:
                </p>
                <div className="mt-5 space-y-3.5">
                  {villainMissing.map((m, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3"
                      data-testid={`villain-missing-${i}`}
                    >
                      <ArrowRight
                        size={16}
                        className="mt-1 shrink-0"
                        style={{ color: "var(--gold-light)" }}
                      />
                      <span
                        className="text-base lg:text-lg"
                        style={{ color: "rgba(248,241,231,0.88)" }}
                      >
                        {m}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHIFT section removed */}
    </>
  );
}
